'use client';

// ADR-070 §1 (refresh proactivo) — SPEC-WEBSOCKET-RECONNECT §3 PR 1.
// Anti-§6: reusa tokenStore + endpoint /auth/refresh existentes.
// 0 lógica inventada.
//
// Comportamiento (SPEC §3 PR 1):
// - Cada 30s verifica el TTL del access token actual (jwtDecode — no
//   verifica firma, solo lee `exp`).
// - Si TTL < 60s (token expira en <1 min), hace POST /api/auth/refresh con
//   credenciales incluidas (httpOnly cookie `profileia.refresh`).
// - Si refresh OK → tokenStore.setAccess(nuevo) → el socket usa el token
//   nuevo en el próximo reconnect (el socket activo sigue con el viejo
//   hasta que el backend lo rechace — per ADR-070 §1).
// - Si refresh falla → best-effort (el socket fallback refresh maneja el
//   error).
// - Mutex refreshingRef: nunca dos refresh en vuelo simultáneos (ADR-070
//   Consecuencias-Negativas-3: "mutex simple").
//
// DESVIACIÓN documentada vs pseudocódigo de la SPEC (2 puntos, ambos para
// alinear con el endpoint REAL — la SPEC es pseudocódigo pre-Task-4):
//   1. URL: `/api/auth/refresh` (NO `/api/v1/auth/refresh`). El backend
//      monta el authRouter en `app.use(env.apiPrefix='/api', v1)` +
//      `v1.use('/auth', authRouter)` (app.ts:412,687) → la ruta real es
//      /api/auth/refresh. Mismo path que usa tryRefresh() en api-client.ts
//      (`${API_PREFIX}/auth/refresh`) y mismo patrón same-origin que el
//      fetch('/api/admin/logout') de auth-context.tsx.
//   2. Envelope: unwrap dual `body.data ?? body` → `.tokens?.accessToken`
//      (?? `.accessToken` plano). El backend responde
//      `{ data: { tokens: { accessToken } } }` (auth.controller.ts:559 +
//      buildAuthResponse) — el pseudocódigo `data.accessToken` directo no
//      matchea el shape real. Mismo patrón dual-envelope que tryRefresh()
//      (AUDIT3-SHARED-009 + 4F-LOW-002, api-client.ts:2061-2071).
//
// Montado UNA vez en el AuthProvider (root layout — patrón P-282). El hook
// es autónomo: CERO dependencia de React state externo.

import { useEffect, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import { tokenStore } from '@/lib/api-client';

export function useTokenRefresh() {
  const refreshingRef = useRef(false);

  useEffect(() => {
    // P-370 fix: extract the async body into a named function + wrap with
    // .catch(). The previous `setInterval(async () => {...})` pattern
    // returns a Promise that is not awaited — if any rejection escapes
    // the try/catch (defensive programming — the try/catch should catch
    // everything, but a future code change could introduce a path that
    // throws outside it), it surfaces as "Uncaught (in promise) undefined"
    // on the console. Wrapping with .catch() ensures no unhandled rejection
    // escapes the interval callback.
    const checkAndRefresh = async () => {
      const token = tokenStore.getAccess();
      if (!token) return;

      try {
        const payload = jwtDecode<{ exp: number }>(token);
        const expiresAtMs = payload.exp * 1000;
        const ttlMs = expiresAtMs - Date.now();

        // Refresh 60s antes de expirar
        if (ttlMs < 60_000 && !refreshingRef.current) {
          refreshingRef.current = true;
          try {
            const res = await fetch('/api/auth/refresh', {
              method: 'POST',
              credentials: 'include', // httpOnly cookie
            });
            if (res.ok) {
              const body: unknown = await res.json();
              // Dual-envelope unwrap (mismo patrón que tryRefresh —
              // api-client.ts H-02/AUDIT3-SHARED-009): el backend
              // responde { data: { tokens: { accessToken } } }.
              const data =
                body && typeof body === 'object'
                  ? ((body as { data?: unknown }).data ?? body)
                  : null;
              const envelope =
                data && typeof data === 'object'
                  ? (data as { tokens?: { accessToken?: unknown }; accessToken?: unknown })
                  : null;
              const accessToken =
                envelope?.tokens?.accessToken ?? envelope?.accessToken;
              if (typeof accessToken === 'string') {
                tokenStore.setAccess(accessToken);
              }
            }
          } catch {
            // Best-effort — el socket fallback refresh maneja el error
          } finally {
            refreshingRef.current = false;
          }
        }
      } catch {
        // Token malformado — dejar que el socket-auth lo rechace
      }
    };
    const interval = setInterval(() => {
      void checkAndRefresh().catch(() => {
        // Defensive: ensures no unhandled rejection escapes the interval
      });
    }, 30_000); // check cada 30s

    return () => clearInterval(interval);
  }, []);
}
